'use client';

import { useEffect, useRef } from 'react';

/**
 * Route-segment error boundary for article pages.
 *
 * Without this, an uncaught client-side error anywhere below this route
 * (e.g. a hydration mismatch — React error #418/#425) leaves Next.js's
 * default behavior: the segment's content is unmounted entirely, while
 * everything outside it (root layout header/nav, layout-level ads) stays
 * visible — which reads as "the page loads then vanishes, only the
 * breadcrumb/ads show". This boundary instead:
 *   1. Logs the error for diagnosis.
 *   2. Attempts one silent automatic retry via reset() — hydration
 *      mismatches are often transient (the retry is a pure client render
 *      with no server HTML to conflict with, so it frequently succeeds).
 *   3. Falls back to a minimal, non-jarring message (not a full crash
 *      page) if the retry also fails, so the reader isn't left staring at
 *      a blank content area.
 */
export default function ArticlePageError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const hasRetriedRef = useRef(false);

  useEffect(() => {
    console.error('[ArticlePage] Client-side render error:', error);

    if (!hasRetriedRef.current) {
      hasRetriedRef.current = true;
      const timer = setTimeout(() => reset(), 50);
      return () => clearTimeout(timer);
    }
  }, [error, reset]);

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
      <p className="text-16-tamil-500 text-4D4D4D">
        இந்தப் பக்கத்தை ஏற்றுவதில் சிக்கல் ஏற்பட்டது.
      </p>
      <button
        onClick={() => reset()}
        className="px-4 py-2 border border-DCDCDC rounded text-14-tamil-600 text-012061 hover:bg-gray-50"
      >
        மீண்டும் முயற்சிக்கவும்
      </button>
    </div>
  );
}
